<template>
  <div class="py-4 container-fluid welcome-page">
    <!-- Header de Bienvenida -->
    <div class="row mb-4">
      <div class="col-12 animate-fade-in-down">
        <div class="welcome-header-card glass-card hover-lift liquid-glass-special">
          <div class="card-body">
            <div class="welcome-header-content">
              <div class="welcome-title-section">
                <div class="welcome-avatar">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    :alt="user.name"
                    class="avatar-image"
                  />
                  <div v-else class="avatar-initials">
                    {{ userInitials }}
                  </div>
                </div>
                <div class="welcome-text">
                  <h4 class="welcome-greeting mb-1">
                    ¡Bienvenido, {{ user.name || 'Usuario' }}!
                  </h4>
                  <p class="welcome-subtitle mb-0">
                    Sistema de Gestión 4CONNECT - {{ getCurrentDate() }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de Módulos Disponibles -->
    <div class="row mb-4" v-if="availableModules.length > 0">
      <div class="col-12">
        <div class="card liquid-glass">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <i class="ri-apps-2-line me-2 text-primary"></i>
              <h6 class="mb-0">Módulos Disponibles</h6>
            </div>
          </div>
          <div class="card-body">
            <div class="modules-grid">
              <div
                v-for="module in availableModules"
                :key="module.id"
                class="module-card"
                @click="navigateToModule(module)"
              >
                <div class="module-icon">
                  <i :class="module.icon"></i>
                </div>
                <div class="module-info">
                  <h6 class="module-title">{{ module.title }}</h6>
                  <p class="module-description">{{ module.description }}</p>
                </div>
                <div class="module-arrow">
                  <i class="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenido enriquecido cuando no hay módulos disponibles -->
    <div class="row mb-4" v-if="availableModules.length === 0">
      <!-- Mensaje principal -->
      <div class="col-12 mb-4">
        <div class="card liquid-glass">
          <div class="card-body text-center py-4">
            <div class="no-modules-content">
              <div class="no-modules-illustration mb-4">
                <div class="illustration-circle">
                  <i class="ri-user-settings-line"></i>
                </div>
              </div>
              <h5 class="mb-3">Configuración de Accesos Pendiente</h5>
              <p class="text-muted mb-4">
                Tu cuenta ha sido creada exitosamente. El administrador del sistema<br>
                configurará tus permisos de acceso según tu rol y responsabilidades.
              </p>
              <div class="action-buttons-group">
                <button class="btn btn-primary btn-lg" @click="refreshPermissions">
                  <i class="ri-refresh-line me-2"></i>
                  Verificar Permisos
                </button>
                <div class="mt-3 contact-info">
                  <p class="mb-1 text-muted small">
                    <i class="ri-information-line me-1"></i>
                    El botón verificará automáticamente si se han asignado nuevos permisos
                  </p>
                  <p class="mb-0 text-muted small">
                    <i class="ri-mail-line me-1"></i>
                    Para solicitar acceso adicional, contacta con el administrador
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Guía para nuevos usuarios -->
      <div class="col-lg-6 mb-4">
        <div class="card liquid-glass info-card hover-lift">
          <div class="card-body">
            <div class="info-header mb-3">
              <div class="info-icon bg-glass">
                <i class="ri-guide-line"></i>
              </div>
              <h6 class="info-title">Guía de Inicio</h6>
            </div>
            <div class="guide-steps">
              <div class="step-item completed">
                <div class="step-number">
                  <i class="ri-check-line"></i>
                </div>
                <div class="step-content">
                  <h6>Cuenta Creada</h6>
                  <p>Tu usuario ha sido registrado correctamente</p>
                </div>
              </div>
              <div class="step-item current">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h6>Asignación de Permisos</h6>
                  <p>Esperando configuración del administrador</p>
                </div>
              </div>
              <div class="step-item">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h6>Acceso Completo</h6>
                  <p>Podrás usar todos los módulos asignados</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendario -->
      <div class="col-lg-6 mb-4">
        <div class="card liquid-glass info-card hover-lift">
          <div class="card-body">
            <div class="info-header mb-3">
              <div class="info-icon bg-glass">
                <i class="ri-calendar-line"></i>
              </div>
              <h6 class="info-title">Calendario</h6>
            </div>
            <div class="calendar-container">
              <!-- Header del calendario -->
              <div class="calendar-header">
                <button class="calendar-nav-btn" @click="previousMonth">
                  <i class="ri-arrow-left-line"></i>
                </button>
                <h6 class="calendar-month-year">
                  {{ currentMonthName }} {{ currentYear }}
                </h6>
                <button class="calendar-nav-btn" @click="nextMonth">
                  <i class="ri-arrow-right-line"></i>
                </button>
              </div>
              
              <!-- Días de la semana -->
              <div class="calendar-weekdays">
                <div class="calendar-weekday" v-for="day in weekdays" :key="day">
                  {{ day }}
                </div>
              </div>
              
              <!-- Grid del calendario -->
              <div class="calendar-grid">
                <div
                  v-for="day in calendarDays"
                  :key="`${day.date}-${day.month}`"
                  class="calendar-day"
                  :class="{
                    'other-month': !day.currentMonth,
                    'today': day.isToday,
                    'selected': day.isSelected
                  }"
                  @click="selectDate(day)"
                >
                  {{ day.day }}
                </div>
              </div>
              
              <!-- Información del día seleccionado -->
              <div class="selected-date-info" v-if="selectedDate">
                <div class="selected-date-header">
                  <i class="ri-calendar-check-line me-2"></i>
                  <span>{{ formatSelectedDate() }}</span>
                </div>
                <p class="selected-date-message">
                  {{ getDateMessage() }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Widgets Útiles -->
    <div class="row mb-4" v-if="availableModules.length > 0">
      <!-- Bloc de Notas -->
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="card liquid-glass info-card hover-lift">
          <div class="card-body">
            <div class="info-header">
              <div class="info-icon bg-glass">
                <i class="ri-sticky-note-line"></i>
              </div>
              <h6 class="info-title">Notas Rápidas</h6>
            </div>
            <div class="notes-content">
              <textarea
                v-model="quickNotes"
                @input="saveNotes"
                class="notes-textarea"
                placeholder="Escribe tus notas aquí... Se guardan automáticamente."
                rows="6"
              ></textarea>
              <div class="notes-info">
                <small class="text-muted">
                  <i class="ri-save-line me-1"></i>
                  Guardado automáticamente
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Temporizador Pomodoro -->
      <div class="col-lg-4 col-md-12 mb-4">
        <div class="card liquid-glass info-card hover-lift">
          <div class="card-body">
            <div class="info-header">
              <div class="info-icon bg-glass">
                <i class="ri-timer-line"></i>
              </div>
              <h6 class="info-title">Pomodoro</h6>
              <div class="pomodoro-session-info">
                <small class="text-muted">Sesión {{ pomodoro.state.session }}</small>
              </div>
            </div>
            <div class="pomodoro-content">
              <div class="timer-display">
                <div class="time-circle" :class="{ 'running': pomodoro.state.isRunning, 'paused': pomodoro.state.isPaused }">
                  <div class="progress-ring">
                    <svg class="progress-svg" width="90" height="90">
                      <circle
                        class="progress-background"
                        cx="45"
                        cy="45"
                        r="40"
                        stroke="rgba(255,255,255,0.2)"
                        stroke-width="3"
                        fill="none"
                      />
                      <circle
                        class="progress-bar"
                        cx="45"
                        cy="45"
                        r="40"
                        stroke="white"
                        stroke-width="3"
                        fill="none"
                        :stroke-dasharray="251.2"
                        :stroke-dashoffset="251.2 - (pomodoro.progress / 100) * 251.2"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                  <span class="time-text">{{ pomodoro.formattedTime }}</span>
                </div>
              </div>
              <div class="timer-mode">
                <span class="mode-text">{{ pomodoro.currentModeLabel }}</span>
                <div class="mode-indicator">
                  <div 
                    class="mode-dot" 
                    :class="{ 
                      'work': pomodoro.state.mode === 'work',
                      'break': pomodoro.state.mode !== 'work'
                    }"
                  ></div>
                </div>
              </div>
              <div class="timer-controls">
                <button
                  class="btn btn-primary btn-sm"
                  @click="pomodoro.canStart ? pomodoro.startTimer() : pomodoro.pauseTimer()"
                  :disabled="pomodoro.state.time === 0 && !pomodoro.state.isRunning"
                >
                  <i :class="pomodoro.state.isRunning ? 'ri-pause-line' : 'ri-play-line'"></i>
                  {{ pomodoro.state.isRunning ? 'Pausar' : 'Iniciar' }}
                </button>
                <button 
                  class="btn btn-secondary btn-sm" 
                  @click="pomodoro.resetTimer()"
                  :disabled="!pomodoro.canReset"
                >
                  <i class="ri-refresh-line"></i>
                  Reiniciar
                </button>
              </div>
              <div class="daily-progress">
                <div class="progress-label">
                  <small class="text-muted">
                    Hoy: {{ pomodoro.stats.today.completed }}/{{ pomodoro.settings.dailyGoal }}
                  </small>
                </div>
                <div class="progress-bar-container">
                  <div 
                    class="progress-bar-fill" 
                    :style="{ width: pomodoro.todayProgress + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import { usePomodoro } from '@/composables/usePomodoro';

export default {
  name: "Welcome",
  setup() {
    const router = useRouter();
    const store = useStore();
    const pomodoro = usePomodoro();
    
    // Estados reactivos
    // const currentTime = ref(new Date()); // Comentado para evitar problemas en Chrome
    
    // Estados de los widgets
    const quickNotes = ref(localStorage.getItem('welcome-notes') || '');
    const shortcuts = ref(JSON.parse(localStorage.getItem('welcome-shortcuts') || '[]'));
    const showShortcutsModal = ref(false);
    

    // Estados del calendario
    const currentDate = ref(new Date());
    const selectedDate = ref(null);
    const weekdays = ref(['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']);
    const monthNames = ref([
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ]);

    // Usuario actual
    const user = computed(() => store.getters['auth/user'] || {});
    
    // Iniciales del usuario
    const userInitials = computed(() => {
      if (!user.value.name) return 'U';
      
      const names = user.value.name.split(' ');
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    });

    // Permisos del usuario
    const userPermissions = computed(() => user.value.permissions || {});

    // Módulos disponibles basados en permisos
    const availableModules = computed(() => {
      const modules = [
        {
          id: 'dashboard',
          title: 'Panel de Control',
          description: 'Vista general del sistema con estadísticas',
          icon: 'ri-dashboard-line',
          route: { name: 'Dashboard' },
          permission: 'dashboard'
        },
        {
          id: 'bulk_update',
          title: 'Actualización Masiva',
          description: 'Herramientas para actualización de datos masiva',
          icon: 'ri-refresh-line',
          route: { name: 'ActualizacionMasiva' },
          permission: 'bulk_update'
        },
        {
          id: 'bulk_loads',
          title: 'Cargas Masivas',
          description: 'Gestión de cargas masivas de información',
          icon: 'ri-file-upload-line',
          route: { name: 'Billing' },
          permission: 'bulk_loads'
        },
        {
          id: 'celsig5',
          title: 'CelSig5 y SCeGR',
          description: 'Gestión de sistemas CelSig5 y SCeGR',
          icon: 'ri-settings-3-line',
          route: { name: 'CelSig5' },
          permission: 'celsig5'
        },
        {
          id: 'connectivity',
          title: 'CON - Conectividad',
          description: 'Módulo de gestión de conectividad',
          icon: 'ri-wifi-line',
          route: { name: 'Conectividad' },
          permission: 'connectivity'
        },
        {
          id: 'locations',
          title: 'Localizaciones Celsig',
          description: 'Gestión de localizaciones del sistema Celsig',
          icon: 'ri-map-pin-line',
          route: { name: 'Localizaciones' },
          permission: 'locations'
        },
        {
          id: 'delete_records',
          title: 'Borrado de Registros',
          description: 'Herramientas para eliminación de registros',
          icon: 'ri-delete-bin-line',
          route: { name: 'BorradoRegistros' },
          permission: 'delete_records'
        },
        {
          id: 'cargar_export',
          title: 'Cargar Export',
          description: 'Carga y exportación de archivos',
          icon: 'ri-upload-cloud-line',
          route: { name: 'CargarExport' },
          permission: 'cargar_export'
        },
        {
          id: 'activity',
          title: 'Actividad',
          description: 'Registro de actividades del sistema',
          icon: 'ri-history-line',
          route: { name: 'Activity' },
          permission: 'activity'
        },
        {
          id: 'users',
          title: 'Usuarios',
          description: 'Gestión de usuarios del sistema',
          icon: 'ri-user-settings-line',
          route: { name: 'Users' },
          permission: 'users'
        },
        {
          id: 'admin',
          title: 'Administración',
          description: 'Panel de administración del sistema',
          icon: 'ri-admin-line',
          route: { name: 'Tables' },
          permission: 'admin'
        }
      ];

      return modules.filter(module => userPermissions.value[module.permission] === true);
    });


    // Computed properties del calendario
    const currentYear = computed(() => currentDate.value.getFullYear());
    
    const currentMonthName = computed(() => monthNames.value[currentDate.value.getMonth()]);
    
    const calendarDays = computed(() => {
      const year = currentDate.value.getFullYear();
      const month = currentDate.value.getMonth();
      
      // Primer día del mes
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Días que necesitamos mostrar del mes anterior
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      // Días que necesitamos mostrar del mes siguiente
      const endDate = new Date(lastDay);
      const remainingDays = 6 - lastDay.getDay();
      endDate.setDate(endDate.getDate() + remainingDays);
      
      const days = [];
      const today = new Date();
      const currentDateObj = new Date(startDate);
      
      while (currentDateObj <= endDate) {
        const dayObj = {
          day: currentDateObj.getDate(),
          date: new Date(currentDateObj),
          month: currentDateObj.getMonth(),
          currentMonth: currentDateObj.getMonth() === month,
          isToday: 
            currentDateObj.getDate() === today.getDate() &&
            currentDateObj.getMonth() === today.getMonth() &&
            currentDateObj.getFullYear() === today.getFullYear(),
          isSelected: selectedDate.value && 
            currentDateObj.getDate() === selectedDate.value.getDate() &&
            currentDateObj.getMonth() === selectedDate.value.getMonth() &&
            currentDateObj.getFullYear() === selectedDate.value.getFullYear()
        };
        
        days.push(dayObj);
        currentDateObj.setDate(currentDateObj.getDate() + 1);
      }
      
      return days;
    });

    // Métodos
    const getCurrentDate = () => {
      return new Intl.DateTimeFormat('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(new Date());
    };


    const getLastUpdate = () => {
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(new Date());
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(dateString));
    };

    const navigateToModule = (module) => {
      router.push(module.route);
    };

    const refreshPermissions = async () => {
      const loadingMessage = ElMessage({
        message: 'Verificando permisos...',
        type: 'info',
        duration: 0, // No auto-close
        showClose: false
      });
      
      try {
        // Intentar cargar permisos desde el store
        await store.dispatch('auth/loadUserPermissions');
        
        // Cerrar mensaje de carga
        loadingMessage.close();
        
        // Verificar si ahora tiene permisos
        const permissions = store.getters['auth/userPermissions'];
        const hasAnyPermission = Object.values(permissions).some(permission => permission === true);
        
        if (hasAnyPermission) {
          ElMessage({
            message: '¡Permisos actualizados! Ya tienes acceso a algunos módulos.',
            type: 'success',
            duration: 4000
          });
          
          // Recargar la página para mostrar los nuevos módulos
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          ElMessage({
            message: 'Aún no tienes permisos asignados. Intenta más tarde.',
            type: 'warning',
            duration: 4000
          });
        }
      } catch (error) {
        // Cerrar mensaje de carga
        loadingMessage.close();
        
        console.error('Error al verificar permisos:', error);
        ElMessage({
          message: 'Error al verificar permisos. Revisa tu conexión.',
          type: 'error',
          duration: 4000
        });
      }
    };


    // Métodos del calendario
    const previousMonth = () => {
      const newDate = new Date(currentDate.value);
      newDate.setMonth(newDate.getMonth() - 1);
      currentDate.value = newDate;
    };

    const nextMonth = () => {
      const newDate = new Date(currentDate.value);
      newDate.setMonth(newDate.getMonth() + 1);
      currentDate.value = newDate;
    };

    const selectDate = (day) => {
      if (day.currentMonth) {
        selectedDate.value = new Date(day.date);
      }
    };

    const formatSelectedDate = () => {
      if (!selectedDate.value) return '';
      
      return new Intl.DateTimeFormat('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(selectedDate.value);
    };

    const getDateMessage = () => {
      if (!selectedDate.value) return '';
      
      const today = new Date();
      const selected = selectedDate.value;
      const diffTime = selected.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return '¡Es hoy! Perfecto para verificar tus permisos.';
      } else if (diffDays === 1) {
        return 'Mañana - Un buen día para revisar el progreso.';
      } else if (diffDays === -1) {
        return 'Fue ayer - Espero que haya sido un buen día.';
      } else if (diffDays > 1 && diffDays <= 7) {
        return `En ${diffDays} días - Marca esta fecha en tu agenda.`;
      } else if (diffDays < -1 && diffDays >= -7) {
        return `Fue hace ${Math.abs(diffDays)} días.`;
      } else if (diffDays > 7) {
        return 'Fecha futura - Planifica con anticipación.';
      } else {
        return 'Fecha del pasado - Los recuerdos son importantes.';
      }
    };


    // Métodos del Bloc de Notas
    const saveNotes = () => {
      localStorage.setItem('welcome-notes', quickNotes.value);
    };



    // Inicialización y actualización de tiempo
    onMounted(() => {
      // Actualizar tiempo cada minuto
      // const interval = setInterval(() => { // Comentado para evitar problemas en Chrome
      //   currentTime.value = new Date();
      // }, 60000);

      // Solicitar permisos de notificación para Pomodoro
      pomodoro.requestNotificationPermission();

      // return () => clearInterval(interval); // Comentado para evitar problemas en Chrome
    });

    return {
      user,
      userInitials,
      userPermissions,
      availableModules,
      getCurrentDate,
      getLastUpdate,
      formatDate,
      navigateToModule,
      refreshPermissions,
      // Variables del calendario
      currentDate,
      selectedDate,
      weekdays,
      monthNames,
      // Computed del calendario
      currentYear,
      currentMonthName,
      calendarDays,
      // Métodos del calendario
      previousMonth,
      nextMonth,
      selectDate,
      formatSelectedDate,
      getDateMessage,
      // Variables de los widgets
      quickNotes,
      shortcuts,
      showShortcutsModal,
      pomodoro,
      // Métodos de los widgets
      saveNotes,
    };
  }
};
</script>

<style scoped>
/* Página de bienvenida */
.welcome-page {
  min-height: 100vh;
  background: transparent;
}

/* Header de bienvenida */
.welcome-header-card {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
  color: white !important;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  backdrop-filter: none !important;
}

.welcome-header-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: shimmer 4s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; }
}

.welcome-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.welcome-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.welcome-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.welcome-avatar .avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.welcome-avatar .avatar-initials {
  color: white;
  font-weight: 700;
  font-size: 1.5rem;
}

.welcome-text .welcome-greeting {
  color: white !important;
  font-weight: 600;
  margin: 0;
}

.welcome-text .welcome-subtitle {
  color: rgba(255, 255, 255, 0.8) !important;
  font-size: 0.9rem;
  margin: 0;
}

.welcome-stats {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 0.9rem;
}

.stat-item i {
  font-size: 1.1rem;
}

.flag-icon {
  font-size: 1.2rem;
  margin-right: 0.5rem;
  display: inline-block;
}

/* Forzar colores blancos en el header independientemente del modo */
.welcome-header-card * {
  color: white !important;
}

.welcome-header-card .welcome-greeting {
  color: white !important;
}

.welcome-header-card .welcome-subtitle {
  color: rgba(255, 255, 255, 0.8) !important;
}

.welcome-header-card .stat-item {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* Grid de módulos */
.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.module-card {
  display: flex;
  align-items: center;
  padding: 1.25rem;
  background: var(--liquid-glass-bg);
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness));
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness));
  border: 1px solid var(--liquid-glass-border);
  border-radius: 16px;
  cursor: pointer;
  transition: var(--liquid-transition-smooth);
  gap: 1rem;
  position: relative;
  overflow: hidden;
}

.module-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.5) 30%, 
    rgba(255, 255, 255, 0.7) 50%, 
    rgba(255, 255, 255, 0.5) 70%, 
    transparent
  );
  z-index: 1;
}

.module-card:hover {
  background: var(--liquid-glass-bg-hover);
  border-color: var(--liquid-glass-border-hover);
  transform: translateY(-3px) scale(1.008);
  box-shadow: var(--liquid-shadow-lg);
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.15) contrast(1.05);
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.15) contrast(1.05);
}

.module-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.module-info {
  flex: 1;
  min-width: 0;
}

.module-title {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
}

.module-description {
  color: var(--bs-text-muted);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
}

.module-arrow {
  color: var(--bs-text-muted);
  font-size: 1.25rem;
  transition: transform 0.3s ease;
}

.module-card:hover .module-arrow {
  transform: translateX(4px);
  color: #4361ee;
}

/* Branding Card */
.brand-card {
  position: relative;
  overflow: hidden;
}

.brand-logo-container {
  position: relative;
  display: inline-block;
}

.brand-logo {
  width: 80px;
  height: auto;
  filter: drop-shadow(0 4px 12px rgba(67, 97, 238, 0.2));
  transition: transform 0.3s ease;
}

.brand-card:hover .brand-logo {
  transform: scale(1.05);
}

.brand-title {
  font-weight: 700;
  color: var(--bs-body-color);
  font-size: 1.5rem;
  margin: 0;
}

.brand-subtitle {
  color: var(--bs-text-muted);
  font-size: 0.9rem;
  line-height: 1.4;
}

.brand-features {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.feature-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: var(--bs-body-color);
}

.feature-item i {
  font-size: 1rem;
}

/* Sin módulos - Versión mejorada */
.no-modules-content {
  max-width: 600px;
  margin: 0 auto;
}

.no-modules-illustration {
  display: flex;
  justify-content: center;
  align-items: center;
}

.illustration-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  position: relative;
  box-shadow: 0 10px 30px rgba(67, 97, 238, 0.3);
}

.illustration-circle::before {
  content: '';
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 2px solid rgba(67, 97, 238, 0.2);
  animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.action-buttons-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.action-buttons-group .btn-lg {
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(67, 97, 238, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.action-buttons-group .btn-lg::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.action-buttons-group .btn-lg:hover::before {
  left: 100%;
}

.action-buttons-group .btn-lg:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(67, 97, 238, 0.4);
}

.action-buttons-group .btn-lg:active {
  transform: translateY(0);
}

.action-buttons-group .btn-lg i {
  font-size: 1.2rem;
  margin-right: 0.5rem;
}

.contact-info {
  max-width: 400px;
  text-align: center;
}

.contact-info p {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  line-height: 1.4;
}

.contact-info i {
  font-size: 0.875rem;
  opacity: 0.8;
}

/* Guía de pasos */
.guide-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 20px;
  top: 50px;
  width: 2px;
  height: 30px;
  background: linear-gradient(to bottom, #e9ecef, transparent);
}

.step-item.completed::after {
  background: linear-gradient(to bottom, #17ad37, #e9ecef);
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.step-item.completed .step-number {
  background: linear-gradient(135deg, #17ad37, #98ec2d);
  color: white;
  font-size: 1rem;
}

.step-item.current .step-number {
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  color: white;
  animation: pulse 2s infinite;
}

.step-item .step-number {
  background: #e9ecef;
  color: var(--bs-text-muted);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.step-content h6 {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0 0 0.25rem 0;
  font-size: 0.95rem;
}

.step-content p {
  color: var(--bs-text-muted);
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
}

/* Lista de recursos */
.resources-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.resource-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 1rem;
}

.resource-item:hover {
  background: rgba(67, 97, 238, 0.1);
  border-color: rgba(67, 97, 238, 0.3);
  transform: translateX(4px);
}

.resource-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #17ad37, #98ec2d);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.resource-content {
  flex: 1;
  min-width: 0;
}

.resource-content h6 {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0 0 0.25rem 0;
  font-size: 0.9rem;
}

.resource-content p {
  color: var(--bs-text-muted);
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.3;
}

.resource-arrow {
  color: var(--bs-text-muted);
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.resource-item:hover .resource-arrow {
  transform: translateX(4px);
  color: #4361ee;
}

/* Estilos del calendario */
.calendar-container {
  max-width: 100%;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.calendar-month-year {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
  text-align: center;
  flex: 1;
}

.calendar-nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--liquid-glass-bg);
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate));
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate));
  border: 1px solid var(--liquid-glass-border);
  color: var(--bs-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--liquid-transition-swift);
  font-size: 1rem;
  box-shadow: var(--liquid-shadow-xs);
}

.calendar-nav-btn:hover {
  background: var(--liquid-glass-bg-hover);
  border-color: var(--liquid-glass-border-hover);
  box-shadow: var(--liquid-shadow-sm);
  transform: scale(1.1);
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.1);
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.1);
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 0.5rem;
}

.calendar-weekday {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bs-text-muted);
  padding: 0.5rem 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 1rem;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: var(--liquid-transition-swift);
  position: relative;
  background: var(--liquid-glass-bg);
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate));
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate));
  border: 1px solid var(--liquid-glass-border);
}

.calendar-day:hover {
  background: var(--liquid-glass-bg-hover);
  border-color: var(--liquid-glass-border-hover);
  box-shadow: var(--liquid-shadow-xs);
  transform: scale(1.05);
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.1);
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.1);
}

.calendar-day.other-month {
  color: var(--bs-text-muted);
  opacity: 0.5;
}

.calendar-day.today {
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  color: white;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
}

.calendar-day.today:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(67, 97, 238, 0.4);
}

.calendar-day.selected {
  background: linear-gradient(135deg, #17ad37, #98ec2d);
  color: white;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(23, 173, 55, 0.3);
}

.calendar-day.selected:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(23, 173, 55, 0.4);
}

.selected-date-info {
  background: var(--liquid-glass-bg);
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness));
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness));
  border: 1px solid var(--liquid-glass-border);
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1rem;
  box-shadow: var(--liquid-shadow-xs);
}

.selected-date-header {
  display: flex;
  align-items: center;
  color: var(--bs-body-color);
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.selected-date-message {
  color: var(--bs-text-muted);
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
  font-style: italic;
}

/* Tarjetas informativas con liquid glass */
.info-card {
  height: 100%;
  background: var(--liquid-glass-bg) !important;
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) contrast(var(--liquid-glass-contrast)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) contrast(var(--liquid-glass-contrast)) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  border-radius: 16px;
  transition: var(--liquid-transition-smooth);
  box-shadow: var(--liquid-shadow-md) !important;
  position: relative;
  overflow: hidden;
}

.info-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.6) 20%, 
    rgba(255, 255, 255, 0.8) 50%, 
    rgba(255, 255, 255, 0.6) 80%, 
    transparent
  );
  z-index: 1;
}

.info-card:hover {
  background: var(--liquid-glass-bg-hover) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(200%) brightness(1.15) contrast(1.1) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(200%) brightness(1.15) contrast(1.1) !important;
  border: 1px solid var(--liquid-glass-border-hover) !important;
  transform: translateY(-4px) scale(1.008);
  box-shadow: var(--liquid-shadow-xl) !important;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.info-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4361ee;
  font-size: 1.25rem;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.info-icon.bg-glass {
  background: var(--liquid-glass-bg-hover) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  box-shadow: var(--liquid-shadow-sm) !important;
}

.info-title {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
}

.info-content {
  color: var(--bs-text-muted);
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 500;
  color: var(--bs-text-muted);
}

.info-value {
  font-weight: 600;
  color: var(--bs-body-color);
}

.help-text {
  color: var(--bs-text-muted);
  font-size: 0.9rem;
  line-height: 1.5;
}

.help-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Notificaciones */
.notifications-list {
  max-height: 200px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #17ad37, #98ec2d);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-text {
  font-size: 0.875rem;
  color: var(--bs-body-color);
  margin: 0 0 0.25rem 0;
  line-height: 1.4;
}

.notification-time {
  color: var(--bs-text-muted);
  font-size: 0.75rem;
}

.no-notifications {
  display: flex;
  align-items: center;
  color: var(--bs-text-muted);
  font-size: 0.9rem;
}

/* Acciones rápidas */
.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.quick-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  color: var(--bs-body-color);
  gap: 0.75rem;
}

.quick-action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(67, 97, 238, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(67, 97, 238, 0.15);
  color: #4361ee;
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  transition: transform 0.3s ease;
}

.quick-action-btn:hover .action-icon {
  transform: scale(1.1);
}

.action-label {
  font-weight: 600;
  font-size: 0.875rem;
  text-align: center;
}

/* Animaciones */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-down {
  animation: fadeInUp 0.6s ease-out;
}

/* Liquid Glass Especial para Header */
.liquid-glass-special {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
  backdrop-filter: blur(40px) saturate(200%) brightness(1.15) contrast(1.1) !important;
  -webkit-backdrop-filter: blur(40px) saturate(200%) brightness(1.15) contrast(1.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  transition: all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1);
  box-shadow: 
    0 1px 0 0 rgba(255, 255, 255, 0.4) inset,
    0 0 0 1px rgba(255, 255, 255, 0.15) inset,
    0 24px 48px rgba(67, 97, 238, 0.3),
    0 12px 24px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.liquid-glass-special::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.1) 0%, 
    transparent 30%, 
    transparent 70%, 
    rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
  z-index: 0;
}

.liquid-glass-special .card-body {
  position: relative;
  z-index: 1;
}

/* Glassmorphism común mejorado */
.glass-card {
  background: var(--liquid-glass-bg) !important;
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  transition: var(--liquid-transition-smooth);
  box-shadow: var(--liquid-shadow-md) !important;
}

/* Excepción para el header de bienvenida */
.welcome-header-card.glass-card {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
}

.glass-card:hover {
  background: var(--liquid-glass-bg-hover) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(200%) brightness(1.15) contrast(1.1) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(200%) brightness(1.15) contrast(1.1) !important;
  border: 1px solid var(--liquid-glass-border-hover) !important;
  box-shadow: var(--liquid-shadow-lg) !important;
  transform: translateY(-2px) scale(1.005);
}

/* Excepción para el hover del header de bienvenida */
.welcome-header-card.glass-card:hover {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.95), rgba(58, 12, 163, 0.95)) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
}

.hover-lift:hover {
  transform: translateY(-4px) scale(1.008);
  transition: var(--liquid-transition-smooth);
  box-shadow: var(--liquid-shadow-xl);
}

/* Modo claro - Asegurar visibilidad */
.welcome-page {
  background: transparent;
}

/* Mejor contraste en modo claro */
.liquid-glass .card-body,
.info-card .card-body {
  position: relative;
  z-index: 2;
}

/* Asegurar legibilidad del texto en modo claro */
.module-card .module-title,
.module-card .module-description {
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.1);
}

/* Modo oscuro */
body.dark-mode-active .welcome-page {
  background: transparent;
}

body.dark-mode-active .info-card {
  background: rgba(26, 32, 44, 0.6) !important;
  backdrop-filter: blur(20px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

body.dark-mode-active .info-card:hover {
  background: rgba(26, 32, 44, 0.8) !important;
  backdrop-filter: blur(25px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(200%) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
}

body.dark-mode-active .module-card {
  background: rgba(26, 32, 44, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .module-card:hover {
  background: rgba(26, 32, 44, 0.8);
  border-color: rgba(79, 195, 247, 0.4);
  box-shadow: 0 8px 25px rgba(79, 195, 247, 0.15);
}

body.dark-mode-active .quick-action-btn {
  background: rgba(26, 32, 44, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .quick-action-btn:hover {
  background: rgba(26, 32, 44, 0.8);
  border-color: rgba(79, 195, 247, 0.4);
  box-shadow: 0 8px 25px rgba(79, 195, 247, 0.15);
  color: #4fc3f7;
}

body.dark-mode-active .glass-card {
  background: rgba(26, 32, 44, 0.6) !important;
  backdrop-filter: blur(20px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

body.dark-mode-active .glass-card:hover {
  background: rgba(26, 32, 44, 0.8) !important;
  backdrop-filter: blur(25px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(200%) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
}

/* Modo oscuro para nuevos elementos */
body.dark-mode-active .brand-logo {
  filter: drop-shadow(0 4px 12px rgba(79, 195, 247, 0.2));
}

body.dark-mode-active .resource-item {
  background: rgba(26, 32, 44, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

body.dark-mode-active .resource-item:hover {
  background: rgba(79, 195, 247, 0.1);
  border-color: rgba(79, 195, 247, 0.3);
}

body.dark-mode-active .step-item .step-number {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

body.dark-mode-active .step-item.current .step-number {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
}

body.dark-mode-active .illustration-circle {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
  box-shadow: 0 10px 30px rgba(79, 195, 247, 0.3);
}

body.dark-mode-active .illustration-circle::before {
  border-color: rgba(79, 195, 247, 0.2);
}

body.dark-mode-active .action-buttons-group .btn-lg {
  box-shadow: 0 8px 25px rgba(79, 195, 247, 0.3);
}

body.dark-mode-active .action-buttons-group .btn-lg:hover {
  box-shadow: 0 12px 35px rgba(79, 195, 247, 0.4);
}

/* Modo oscuro para el calendario */
body.dark-mode-active .calendar-nav-btn {
  background: rgba(79, 195, 247, 0.1);
  color: #4fc3f7;
}

body.dark-mode-active .calendar-nav-btn:hover {
  background: rgba(79, 195, 247, 0.2);
}

body.dark-mode-active .calendar-day {
  background: rgba(26, 32, 44, 0.3);
  border-color: rgba(255, 255, 255, 0.05);
}

body.dark-mode-active .calendar-day:hover {
  background: rgba(79, 195, 247, 0.1);
  border-color: rgba(79, 195, 247, 0.2);
}

body.dark-mode-active .calendar-day.today {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
  box-shadow: 0 4px 12px rgba(79, 195, 247, 0.3);
}

body.dark-mode-active .calendar-day.today:hover {
  box-shadow: 0 6px 16px rgba(79, 195, 247, 0.4);
}

body.dark-mode-active .selected-date-info {
  background: rgba(26, 32, 44, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Asegurar que el header de bienvenida mantenga el gradiente en modo oscuro */
body.dark-mode-active .welcome-header-card.glass-card {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.9), rgba(58, 12, 163, 0.9)) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
}

body.dark-mode-active .welcome-header-card.glass-card:hover {
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.95), rgba(58, 12, 163, 0.95)) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
}

/* Responsive */
@media (max-width: 768px) {
  .welcome-header-content {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }

  .welcome-title-section {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .welcome-stats {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }

  .stat-item {
    font-size: 0.85rem;
  }

  .modules-grid {
    grid-template-columns: 1fr;
  }

  .quick-actions-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .quick-action-btn {
    padding: 1rem 0.75rem;
  }

  .action-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .welcome-avatar {
    width: 50px;
    height: 50px;
  }

  .welcome-avatar .avatar-initials {
    font-size: 1.25rem;
  }

  .quick-actions-grid {
    grid-template-columns: 1fr;
  }

  .module-card {
    padding: 1rem;
  }

  .module-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  /* Calendario responsive */
  .calendar-header {
    margin-bottom: 0.75rem;
  }

  .calendar-month-year {
    font-size: 0.9rem;
  }

  .calendar-nav-btn {
    width: 28px;
    height: 28px;
    font-size: 0.875rem;
  }

  .calendar-weekday {
    font-size: 0.65rem;
    padding: 0.375rem 0.125rem;
  }

  .calendar-day {
    font-size: 0.8rem;
    border-radius: 6px;
  }

  .selected-date-info {
    padding: 0.75rem;
    margin-top: 0.75rem;
  }

  .selected-date-header {
    font-size: 0.85rem;
  }

  .selected-date-message {
    font-size: 0.8rem;
  }
}

/* Estilos para los nuevos widgets */


/* Bloc de Notas con liquid glass */
.notes-content {
  min-height: 140px;
}

.notes-textarea {
  width: 100%;
  border: 1px solid var(--liquid-glass-border) !important;
  border-radius: 12px;
  padding: 0.75rem;
  font-size: 0.85rem;
  line-height: 1.4;
  resize: none;
  background: var(--liquid-glass-bg-hover) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-light)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  color: var(--bs-body-color) !important;
  transition: var(--liquid-transition-swift);
  box-shadow: var(--liquid-shadow-sm) !important;
}

.notes-textarea:focus {
  outline: none;
  border-color: var(--bs-primary) !important;
  background: var(--liquid-glass-bg-active) !important;
  backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.15) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur)) saturate(200%) brightness(1.15) !important;
  box-shadow: 
    0 0 0 0.2rem rgba(67, 97, 238, 0.25),
    var(--liquid-shadow-md) !important;
}

.notes-textarea::placeholder {
  color: var(--bs-text-muted) !important;
  font-style: italic;
}

.notes-info {
  text-align: right;
  margin-top: 0.5rem;
}

.notes-info small {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

/* Temporizador Pomodoro */
.pomodoro-session-info {
  margin-left: auto;
}

.pomodoro-content {
  text-align: center;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.timer-display {
  margin-bottom: 1rem;
}

.time-circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #17ad37, #98ec2d);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  position: relative;
  overflow: visible;
  transition: all 0.3s ease;
}

.time-circle.running {
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  animation: pulse 2s infinite;
}

.time-circle.paused {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.progress-ring {
  position: absolute;
  top: -5px;
  left: -5px;
  width: 100px;
  height: 100px;
  transform: rotate(-90deg);
}

.progress-svg {
  width: 100%;
  height: 100%;
}

.progress-bar {
  transition: stroke-dashoffset 0.3s ease;
}

.time-circle::before {
  content: '';
  position: absolute;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 1;
}

.time-text {
  font-size: 1rem;
  font-weight: 700;
  color: #17ad37;
  position: relative;
  z-index: 2;
}

.time-circle.running .time-text {
  color: #4361ee;
}

.time-circle.paused .time-text {
  color: #f59e0b;
}

.timer-mode {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.mode-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--bs-body-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mode-indicator {
  display: flex;
  justify-content: center;
}

.mode-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.mode-dot.work {
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  box-shadow: 0 0 10px rgba(67, 97, 238, 0.5);
}

.mode-dot.break {
  background: linear-gradient(135deg, #17ad37, #98ec2d);
  box-shadow: 0 0 10px rgba(23, 173, 55, 0.5);
}

.timer-controls {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.timer-controls .btn {
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.3s ease;
}

.timer-controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.daily-progress {
  margin-top: auto;
}

.progress-label {
  text-align: center;
  margin-bottom: 0.5rem;
}

.progress-bar-container {
  width: 100%;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #17ad37, #98ec2d);
  border-radius: 2px;
  transition: width 0.3s ease;
}


/* Accesos Directos */
.shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
}

.shortcut-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 0.75rem;
  position: relative;
}

.shortcut-item:hover {
  background: rgba(255, 255, 255, 0.5);
  border-color: rgba(67, 97, 238, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.shortcut-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
}

.shortcut-info {
  flex: 1;
  min-width: 0;
}

.shortcut-title {
  font-weight: 600;
  color: var(--bs-body-color);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shortcut-url {
  font-size: 0.75rem;
  color: var(--bs-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shortcut-delete {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0;
  font-size: 0.8rem;
}

.shortcut-item:hover .shortcut-delete {
  opacity: 1;
}

.shortcut-delete:hover {
  background: rgba(220, 53, 69, 0.2);
  transform: scale(1.1);
}

.no-shortcuts {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-text-muted);
  font-size: 0.9rem;
  padding: 2rem;
}

/* Modo oscuro para los nuevos widgets */
body.dark-mode-active .notes-textarea {
  background: rgba(26, 32, 44, 0.6) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
  box-shadow: 
    0 2px 10px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05) !important;
}

body.dark-mode-active .notes-textarea:focus {
  background: rgba(26, 32, 44, 0.8) !important;
  backdrop-filter: blur(15px) !important;
  -webkit-backdrop-filter: blur(15px) !important;
  border-color: #4fc3f7 !important;
  box-shadow: 
    0 0 0 0.2rem rgba(79, 195, 247, 0.25),
    0 4px 20px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

body.dark-mode-active .notes-textarea::placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
  font-style: italic;
}


body.dark-mode-active .shortcut-item {
  background: rgba(26, 32, 44, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .shortcut-item:hover {
  background: rgba(26, 32, 44, 0.6);
  border-color: rgba(79, 195, 247, 0.3);
}

body.dark-mode-active .shortcut-icon {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
}

body.dark-mode-active .info-icon.bg-glass {
  background: rgba(26, 32, 44, 0.8) !important;
  backdrop-filter: blur(15px) !important;
  -webkit-backdrop-filter: blur(15px) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
}

body.dark-mode-active .info-icon {
  color: #4fc3f7;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
}

body.dark-mode-active .time-circle {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
}

body.dark-mode-active .time-circle.running {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
}

body.dark-mode-active .time-circle.paused {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

body.dark-mode-active .time-circle::before {
  background: rgba(26, 32, 44, 0.9);
}

body.dark-mode-active .time-text {
  color: #4fc3f7;
}

body.dark-mode-active .time-circle.running .time-text {
  color: #4fc3f7;
}

body.dark-mode-active .time-circle.paused .time-text {
  color: #fbbf24;
}

body.dark-mode-active .mode-dot.work {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
  box-shadow: 0 0 10px rgba(79, 195, 247, 0.5);
}

body.dark-mode-active .mode-dot.break {
  background: linear-gradient(135deg, #4fc3f7, #29b6f6);
  box-shadow: 0 0 10px rgba(79, 195, 247, 0.5);
}

body.dark-mode-active .progress-bar-container {
  background: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .progress-bar-fill {
  background: linear-gradient(90deg, #4fc3f7, #29b6f6);
}

/* Responsive para los nuevos widgets */
@media (max-width: 768px) {
  .shortcuts-grid {
    grid-template-columns: 1fr;
  }
  
  .shortcut-item {
    padding: 0.5rem;
  }
  
  .shortcut-title {
    font-size: 0.85rem;
  }
  
  .shortcut-url {
    font-size: 0.7rem;
  }
  
  .time-circle {
    width: 60px;
    height: 60px;
  }
  
  .time-circle::before {
    width: 50px;
    height: 50px;
  }
  
  .time-text {
    font-size: 0.85rem;
  }
  
}

@media (max-width: 480px) {
  .timer-controls {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .timer-controls .btn {
    width: 100%;
  }
}
</style>